import api from './api';
import { saveAs } from '../utils/saveFile';

// Accessorial Upload (360 port of v1 imports/accessorialupload.pl).
// Endpoints live under /accessorial-import/*. The axios `api` instance already
// carries the /api/v1 prefix.
const BASE = '/accessorial-import';

export interface RowError {
  row: number;
  field: string;
  message: string;
}

export interface AccessorialReferenceData {
  columns: string[];
  required_columns: string[];
  all_gl_codes: string[];
  dtd_gl_codes: string[];
  dtd_item_name: string;
  rate_types: string[];
  paperwork_flags: string[];
}

export interface LineItemPreview {
  row: number;
  line_number: number;
  item: string;        // what gets stored (invoice_lineitem.liitem)
  item_label: string;  // what the sheet said (the display name)
  gl_code: string;
  qty: string;
  rate: string;
  rate_type: string;
  description: string;
  total: string;
}

export interface DuplicateMatch {
  recno: number;
  accessorial_num: number;
  created_by: string;
  created_at: string;
  total: string;
}

export interface AccessorialPreview {
  pronumber: number;
  mas_tag: string;
  billto: number;
  billto_name: string;
  quote_number: number;
  revision_number: number;
  billto_ref_no: string;
  invoice_comments: string;
  flags: Record<string, string>;
  rows: number[];
  line_items: LineItemPreview[];
  line_count: number;
  total: string;
  /** "<pronumber>|<mas tag>" — what apply() takes to skip this one. */
  key: string;
  duplicate_of: DuplicateMatch | null;
}

export interface ValidateResponse {
  filename: string;
  total_rows: number;
  invoice_count: number;
  line_count: number;
  grand_total: string;
  duplicate_count: number;
  accessorials: AccessorialPreview[];
  warnings: RowError[];
}

export interface CreatedAccessorial {
  pronumber: number;
  mas_tag: string;
  recno: number;
  accessorial_num: number;
  line_count: number;
  total: string;
}

export interface ApplyResponse {
  filename: string;
  invoice_count: number;
  line_count: number;
  grand_total: string;
  created: CreatedAccessorial[];
  skipped_count: number;
  warnings: RowError[];
  detail?: string | null;
}

/** Pull the row-level error list out of a 422 the importer raised. */
export function extractRowErrors(e: unknown): RowError[] {
  const detail = (e as { response?: { data?: { detail?: { errors?: RowError[] } } } })
    ?.response?.data?.detail;
  if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) return detail.errors;
  return [];
}

/** Human-readable message for anything that isn't a row-level 422. */
export function extractDetail(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (e instanceof Error) return e.message;
  return String(e);
}

const upload = (file: File): FormData => {
  const fd = new FormData();
  fd.append('file', file);
  return fd;
};

export const accessorialImportService = {
  async getReferenceData(): Promise<AccessorialReferenceData> {
    const { data } = await api.get<AccessorialReferenceData>(`${BASE}/reference-data`);
    return data;
  },

  /** Download the blank 19-column form. */
  async downloadForm(): Promise<void> {
    await saveAs('accessorial-form.xlsx', async () => {
      const response = await api.get(`${BASE}/template`, { responseType: 'blob' });
      return new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  },

  /** Validate an upload and get back what it WOULD create. Writes nothing. */
  async validate(file: File): Promise<ValidateResponse> {
    const { data } = await api.post<ValidateResponse>(`${BASE}/validate`, upload(file), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /**
   * Create every accessorial the file describes. The file is posted again
   * rather than a token: the server re-validates at commit, so anything that
   * changed since the preview (an expired quote, a deleted order) is caught.
   */
  async apply(file: File, skipKeys: string[] = []): Promise<ApplyResponse> {
    const fd = upload(file);
    // Omitted entirely when nothing is skipped, so the request stays identical to
    // what a caller that knows nothing about skipping would send.
    if (skipKeys.length) fd.append('skip', JSON.stringify(skipKeys));
    const { data } = await api.post<ApplyResponse>(`${BASE}/apply`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

export default accessorialImportService;
